<template>
  <div class="mt-4">
    <div v-if="rates.length > 0" class="rates-table w-100">
      <div class="card card-cascade narrower">
        <div
          ref="ratesTableHeader"
          class="
            calc-rates-table-panel
            view
            view-cascade
            gradient-card-header
            aqua-gradient
            narrower
            py-2
            mx-4
            mb-3
            d-flex
            flex-column
            align-items-around
          "
        >
          <ul
            v-if="grand.length > 0"
            class="list-unstyled text-lg-center mt-3 grand-total float-left"
          >
            <li
              v-for="(v, i) in grand"
              :key="i"
            >{{v.name}}: {{v.value}}</li>
          </ul>
          <div class="d-flex justify-content-between align-items-center">
            <mdb-btn
              v-if="isUser"
              rounded
              size="sm"
              class="select-button"
              :disabled="selectedRates.length === 0"
              gradient="deep-blue"
              title="Create order with selected rates"
              @click.native="onCreateOrderButtonClicked"
            >Create Order</mdb-btn>
            <div>
              <mdb-btn-group>
                <mdb-btn
                  disabled
                  rounded
                  size="sm"
                  gradient="deep-blue"
                  title="Not implemented yet"
                >Filter</mdb-btn>
                <mdb-btn
                  disabled
                  rounded
                  size="sm"
                  gradient="deep-blue"
                  title="Not implemented yet"
                >Sort</mdb-btn>
                <mdb-btn
                  disabled
                  rounded
                  size="sm"
                  gradient="deep-blue"
                  title="Not implemented yet"
                >Search</mdb-btn>
              </mdb-btn-group>
              <mdb-dropdown multi-level title="Add columns">
                <mdb-dropdown-toggle
                  rounded
                  size="sm"
                  slot="toggle"
                  gradient="aqua">+</mdb-dropdown-toggle>
                <mdb-dropdown-menu>
                  <ul class="list-unstyled">
                    <li
                      v-for="(column, index) in allColumns.filter(col => col.index !== null)"
                      :key="`columns-checkbox-${index}`"
                    >
                      <mdb-input
                        type="checkbox"
                        :id="`columns-checkbox-${index}`"
                        :name="`columns-checkbox-${index}`"
                        :label="column.label"
                        v-model="selectedColumns[column.index]"
                        @change="onColumnChange()"
                      />
                    </li>
                  </ul>
                </mdb-dropdown-menu>
              </mdb-dropdown>
            </div>
          </div>
        </div>
        <calc-rates-table
          :data="{columns: columns, rows: rates}"
          max-width="95%"
          min-width="95%"
          :responsive="false"
          :selected-optionals="selectedOptionals"
          @selected-optionals-change="onSelectedOptionalsChange($event)"
          @selected-optionals-set="onSelectedOptionalsSet($event)"
          @hide-column="onColumnChange($event, false)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import {
  mdbBtnGroup,
  mdbDropdown,
  mdbDropdownToggle,
  mdbDropdownMenu,
  mdbBtn,
  mdbInput,
  mdbContainer,
  mdbIcon,
  mdbSelect
} from 'mdbvue'
import CalcRatesTable from './CalcRatesTable'
import Bridge8Select from './Bridge8Select'
import {mapState, mapGetters, mapActions} from 'vuex'
import {calculateTotalWithOptional, errorCatch, orderType} from '../utils'
import {OrderTypes} from '../enums'

export default {
  name: 'CalcRates',
  data () {
    return {
      selectedOptionals: [],
      allColumns: [
        {
          index: null,
          label: 'Select',
          rowspan: true,
          componentName: 'CalcRatesTableCellCheckbox',
          sortCallback: (row, params) => params.selected.includes(row.map(r => r.rateData.rate.uuid).join(',')) ? 0 : 1,
          sort: 'desc'
        },
        {
          index: null,
          label: 'Recommended',
          rowspan: true,
          componentName: 'CalcRatesTableCellRecommended',
          sortCallback: row => {
            if (row.length <= 1) return 0
            const arr = row.map(r => r.rateData.rate.agent.name)
            return arr.every(a => a === arr[0]) ? 1 : 0
          },
          sort: 'desc'
        },
        {
          index: 1,
          label: 'Rate',
          componentName: 'CalcRatesTableCellName',
          sortCallback: row => {
            return [
              row[0].rateData.rate.origin,
              ' - ',
              row[0].rateData.rate.destination,
              ' by ',
              row[0].rateData.rate.airline.title,
              '/',
              row[0].rateData.rate.agent.name
            ].join('')
          },
          sort: 'asc'
        },
        {
          index: 2,
          label: 'Included',
          componentName: 'CalcRatesTableCellInclude',
          sortCallback: row => row.flatMap(r => r.rateData.rate.include).length,
          sort: 'desc'
        },
        {
          index: 3,
          label: 'Extra',
          componentName: 'CalcRatesTableCellExtra',
          sortCallback: row => row.flatMap(r => r.rateData.rate.extra).length,
          sort: 'desc'
        },
        {
          index: 4,
          label: 'Optional',
          componentName: 'CalcRatesTableCellOptional',
          sortCallback: row => row.flatMap(r => r.rateData.rate.optional).length,
          sort: 'desc'
        },
        {
          index: 5,
          label: 'Transit time',
          componentName: 'CalcRatesTableCellTransitTime',
          sortCallback: row => row[0].rateData.rate.transit_time,
          sort: 'asc'
        },
        {
          index: 6,
          label: 'Weekdays',
          componentName: 'CalcRatesTableCellWeekdays',
          sortCallback: row => row.flatMap(r => r.rateData.rate.weekdays).length,
          sort: 'desc'
        },
        {
          index: 7,
          label: 'Validity',
          componentName: 'CalcRatesTableCellValidity',
          sortCallback: row => row[0].rateData.rate.validity.from,
          sort: 'desc'
        },
        {
          index: 8,
          label: 'Comment',
          componentName: 'CalcRatesTableCellComment',
          sortCallback: row => row.map(r => r.rateData.rate.comment).join('\n'),
          sort: 'asc'
        },
        {
          index: 9,
          label: 'Total',
          componentName: 'CalcRatesTableCellTotal',
          sortCallback: (row, params) => {
            return row.map(r => calculateTotalWithOptional(r, params)).reduce((a, c) => a + c, 0)
          },
          sort: 'asc'
        },
        {
          index: 10,
          label: 'Margin',
          componentName: 'CalcRatesTableCellMargin',
          sortCallback: row => row[0].margin,
          sort: 'desc'
        }
      ],
      selectedColumns: []
    }
  },
  computed: {
    ...mapState('user', {
      stateSelectedColumns: state => state.ratesTableColumns
    }),
    ...mapGetters('user', ['isAuthenticated', 'isUser']),
    ...mapState('calc', {
      client: state => state.query.client,
      agent: state => state.query.agent,
      qid: state => state.query.qid,
      rates: state => state.ratesData,
      selectedRates: state => state.selectedRates,
      grand: state => state.grand,
      linked: state => state.linked,
      clone: state => state.clone
    }),
    columns: function () {
      return this.allColumns.filter((column, index) => {
        return column.index === null || this.selectedColumns[column.index]
      })
    }
  },
  methods: {
    ...mapActions(['apiOrdersCreate']),
    ...mapActions('user', ['setRatesTableColumns']),
    ...mapActions('calc', [
      'clearQuery',
      'clearSelectedRates',
      'setLinked',
      'setClone',
      'setClient',
      'setAgent'
    ]),
    onSelectedOptionalsChange: ({optional, value}) => {
      if (value) {
        this.$set(this.selectedOptionals, this.selectedOptionals.length, optional)
      } else {
        this.selectedOptionals = this.selectedOptionals.filter(opt => opt !== optional)
      }
    },
    onSelectedOptionalsSet: selectedOptionals => {
      this.selectedOptionals = selectedOptionals
    },
    onColumnChange (index = null, value = null) {
      if (index !== null && value !== null) {
        this.$set(this.selectedColumns, index, value)
      }
      const newSelectedColumns = []
      for (let [key, value] of Object.entries(this.selectedColumns)) {
        if (value) {
          newSelectedColumns.push(parseInt(key))
        }
      }
      if (JSON.stringify(this.stateSelectedColumns) !== JSON.stringify(newSelectedColumns)) {
        this.setRatesTableColumns(newSelectedColumns)
          .then(() => {
            if (this.isAuthenticated) {
              this.$notify.info({
                title: 'Rates Table Columns are saved.',
                message: '',
                position: 'top right',
                timeOut: 5000,
                closeButton: true
              })
            }
          })
          .catch(errorCatch('Rates Table Columns save', this))
      }
    },
    onCreateOrderButtonClicked () {
      if (orderType(this.linked) === OrderTypes.AGENT && !this.client) {
        this.$notify.warn({
          title: 'Select Client.',
          message: 'You have to select client while creating order in current context.',
          position: 'top right',
          closeButton: true
        })
        return
      }
      // TODO: VERY bad solution?
      const data = this.rates.filter(rateItems => {
        return this.selectedRates.includes(rateItems.map(item => item.rateData.rate.uuid).join(','))
      })
        .map(rateItems => {
          return rateItems.map(rateItem => {
            return {
              price: calculateTotalWithOptional(rateItem, {selectedOptionalTypes: this.selectedOptionals}),
              agent: rateItem.rateData.rate.agent && rateItem.rateData.rate.agent.name,
              uuid: rateItem.rateData.rate.uuid
            }
          })
        })
      // по цепочке: (clone === null && linked === null && clinet !== null) || client !== null && clone.context === CLIENT
      this.apiOrdersCreate({
        linked: this.linked ? this.linked.info.uuid : null,
        clone: this.clone ? {
          context: this.clone.context || null,
          order: (this.clone.order && this.clone.order.info.uuid) || null
        } : null,
        client: this.client,
        qid: this.qid,
        data: data
      })
        .then(() => {
          this.setLinked(null)
          this.setClone(null)
          this.setAgent(null)
          this.setClient(null)
          this.clearSelectedRates()
            .then(() => {
              this.clearQuery()
                .then(() => {
                  this.$notify.success({
                    title: 'Order created.',
                    message: '',
                    position: 'top right',
                    closeButton: true
                  })
                  this.$router.push('/orders')
                })
            })
        })
        .catch(errorCatch('Order Creation failed', this))
    }
  },
  mounted () {
    this.$bus.$on('calculate-completed', () => {
      setTimeout(() => { this.$refs.ratesTableHeader.scrollIntoView() }, 500)
    })
    const calculateSelectedColumns = () => {
      const selectedColumns = {}
      this.allColumns.filter(col => col.index !== null)
        .forEach(col => {
          selectedColumns[col.index] = this.stateSelectedColumns.includes(col.index)
        })
      this.selectedColumns = selectedColumns
    }
    this.$bus.$on('profile-fetch-completed', calculateSelectedColumns)
    calculateSelectedColumns()
  },
  components: {
    Bridge8Select,
    mdbContainer,
    mdbIcon,
    mdbBtn,
    mdbInput,
    mdbBtnGroup,
    mdbDropdown,
    mdbDropdownToggle,
    mdbDropdownMenu,
    CalcRatesTable,
    mdbSelect
  }
}
</script>

<style lang="sass" scoped>
.dropdown-toggle:after
  display: none

.grand-total
  color: whitesmoke
  text-transform: uppercase

.select-button
  color: darkslategray

  &:hover
    color: black
</style>

<style lang="sass">
.calc-rates-table-panel
  overflow: visible

  td
    word-break: break-word

.rates-table
  animation: fadein 2s

@keyframes fadein
  from
    opacity: 0
  to
    opacity: 1

.rates-additional-info
  font-size: 85%

.price-display
  font-weight: 700
  font-size: smaller
</style>



// WEBPACK FOOTER //
// src/components/CalcRates.vue